import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery
} from "@tanstack/react-query";

import { getErrorMessage } from "utils/error";
import addressApi, { QUERY_SYNC_UNITS_STATUS } from "api/landlord/address";
import { instance } from "api/apiBase";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import {
  Address,
  PaginatedResponse,
  PropertyKind,
  PropertyManagementSystemSyncStatus
} from "shared/types";
import { Unit } from "types/landlord/address";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { toast } from "react-toastify";
import { useState } from "react";
import { BlankUnit } from "./types";
import { SortingState } from "@tanstack/react-table";

const DEFAULT_PER_PAGE = window.localStorage.getItem("per_page") || 10;

export const useUnitsTableController = ({
  query,
  isSyncing = false,
  available_for_application = null,
  sorting = []
}: {
  query: string;
  isSyncing?: boolean;
  available_for_application?: boolean | null;
  sorting?: SortingState;
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 500);
  const [searchParams, setSearchParams] = useSearchParamsState(
    {
      unit: "",
      page: "1",
      per_page: String(DEFAULT_PER_PAGE)
    },
    ["unit", "page", "per_page"]
  );

  const [debouncedParams] = useDebouncedValue(searchParams.unit, 500);

  const { data, refetch, isFetching, isRefetching } = useQuery({
    queryKey: [query, { ...searchParams, unit: debouncedParams, sorting }],
    queryFn: () =>
      instance
        .get(query, {
          params: {
            page: searchParams.page,
            search: debouncedParams,
            available_for_application,
            per_page: searchParams.per_page,
            order_direction: sorting[0]?.desc ? "desc" : "asc"
          }
        })
        .then(({ data }: { data: PaginatedResponse<Unit> }) => data),
    placeholderData: keepPreviousData
  });

  const queryProperties = useInfiniteQuery<
    PaginatedResponse<{
      kind: PropertyKind | null;
      address: Address;
      id: number;
      magic_link: string | null;
      name: string;
      total_applications: number;
      total_units: number;
    }>
  >({
    queryKey: ["infinite_properties", { searchTerm: debouncedSearchTerm }],
    queryFn: ({ pageParam }) =>
      addressApi
        .getProperties({
          page: Number(pageParam),
          searchTerm: debouncedSearchTerm
        })
        .catch((err) => {
          toast.error(getErrorMessage(err));
        }),
    initialPageParam: 1,
    getNextPageParam: (
      lastPage: PaginatedResponse<{
        address: Address;
        id: number;
        magic_link: string | null;
        name: string;
        total_applications: number;
        total_units: number;
      }> | null
    ) =>
      lastPage && lastPage.has_more ? (lastPage.current_page || 0) + 1 : null
  });

  const { data: syncData, isError: syncError } =
    useQuery<PropertyManagementSystemSyncStatus>({
      queryKey: [QUERY_SYNC_UNITS_STATUS],
      queryFn: () => addressApi.getPropertyManagementSystemSyncStatus("units"),
      refetchInterval: 4000,
      enabled: isSyncing
    });

  return {
    data,
    refetch,
    debouncedParams,
    searchParams,
    setSearchParams,
    queryProperties,
    isFetching,
    isRefetching,
    syncData,
    syncError,
    setSearchTerm,
    searchTerm
  };
};
